/**
 * Выбор квадратной области для аватара.
 *
 * Люди грузят что попало: панораму, вертикальный портрет, скриншот. Обрезать
 * такое автоматически по центру — значит регулярно отрезать голову. Здесь
 * человек сам двигает рамку и меняет её размер, а вырезанный квадрат уже идёт
 * в загрузку.
 *
 * Рамка всегда квадратная и не выходит за края кадра: аватар круглый, и любая
 * другая форма всё равно обрежется.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Crop {
  x: number;
  y: number;
  size: number;
}

interface Props {
  file: File;
  onCancel: () => void;
  onApply: (crop: Crop) => void;
}

export function AvatarCropper({ file, onCancel, onApply }: Props) {
  const { t } = useTranslation();
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [crop, setCrop] = useState<Crop | null>(null);
  const frame = useRef<HTMLDivElement>(null);

  // Ссылка считается во время рендера, а освобождается при размонтировании:
  // иначе каждый выбранный файл висит в памяти вкладки до её закрытия.
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // По умолчанию берём наибольший квадрат по центру: чаще всего он и нужен.
  const ready = (width: number, height: number) => {
    const size = Math.min(width, height);
    setNatural({ width, height });
    setCrop({ x: (width - size) / 2, y: (height - size) / 2, size });
  };

  /** Перетаскивание рамки и изменение её размера в координатах изображения. */
  const startDrag = useCallback(
    (event: React.PointerEvent, mode: "move" | "resize") => {
      if (!crop || !natural || !frame.current) return;

      event.preventDefault();
      event.stopPropagation();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);

      const box = frame.current.getBoundingClientRect();
      // Кадр показан уменьшенным: движения мыши переводятся в пиксели
      // оригинала, иначе рамка отстаёт от курсора тем сильнее, чем крупнее файл.
      const scale = natural.width / box.width;
      const startX = event.clientX;
      const startY = event.clientY;
      const origin = { ...crop };

      const onMove = (move: PointerEvent) => {
        const dx = (move.clientX - startX) * scale;
        const dy = (move.clientY - startY) * scale;

        setCrop(() => {
          if (mode === "resize") {
            const limit = Math.min(natural.width - origin.x, natural.height - origin.y);
            const size = Math.max(64, Math.min(limit, origin.size + dx));
            return { ...origin, size };
          }

          return {
            size: origin.size,
            x: Math.max(0, Math.min(natural.width - origin.size, origin.x + dx)),
            y: Math.max(0, Math.min(natural.height - origin.size, origin.y + dy)),
          };
        });
      };

      const onUp = (up: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        if (handle.hasPointerCapture?.(up.pointerId)) handle.releasePointerCapture(up.pointerId);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [crop, natural],
  );

  const percent = (value: number, total: number) => `${(value / total) * 100}%`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/85 p-4 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" className="panel w-full max-w-lg overflow-hidden">
        <div className="flex items-start justify-between gap-4 border-b border-ink-800 px-5 py-3">
          <h2 className="font-sans text-xl text-ink-50">{t("avatar.cropTitle")}</h2>
          <button type="button" className="btn-ghost px-2 py-1 text-sm" onClick={onCancel}>
            ✕
          </button>
        </div>

        <div className="space-y-4 p-5">
          <p className="text-xs text-ink-300">{t("avatar.cropHint")}</p>

          {/* Рамка обхватывает само изображение, а не контейнер: иначе
              у вертикального кадра квадрат превращается в овал по ширине
              пустых полей. */}
          <div className="flex justify-center">
            <div ref={frame} className="relative inline-block overflow-hidden rounded-xl bg-ink-950">
              {url && (
                <img
                  src={url}
                  alt=""
                  className="block max-h-[52vh] max-w-full"
                  onLoad={(event) =>
                    ready(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight)
                  }
                />
              )}

              {crop && natural && (
                <div
                  className="absolute cursor-move rounded-full border-2 border-amber-400 shadow-[0_0_0_9999px_var(--overlay-veil)]"
                  style={{
                    left: percent(crop.x, natural.width),
                    top: percent(crop.y, natural.height),
                    width: percent(crop.size, natural.width),
                    height: percent(crop.size, natural.height),
                  }}
                  onPointerDown={(event) => startDrag(event, "move")}
                >
                  <span
                    role="presentation"
                    className="absolute -bottom-1 -right-1 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-ink-950 bg-amber-400"
                    onPointerDown={(event) => startDrag(event, "resize")}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={onCancel}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!crop}
              onClick={() => crop && onApply(crop)}
            >
              {t("avatar.apply")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
